import type { ConsumerState } from '../../state'
import type { AppleGatewayIssueCardRequest, AppleGatewaySaveOfferRequest, PresentedOffer, UserCohort, InterestDetails } from '../../state/appleGateway/apis'
import { PaymentType } from '@afterpay/types'
import { v4 as uuidv4 } from 'uuid'
import type { EnrichedOfferDetail } from './hooks'
import { isValidString, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { formatUtcToTimezoneOffset } from '@afterpaytouch/core'
import { app, api } from '../../env'

export const parseHasInstoreCard = (value: string | undefined): boolean => {
  return value === 'true'
}

const APPLE_PAYLATER_TIMEZONE = 'America/Los_Angeles'

const buildSelectedOfferLink = (cohort: UserCohort, token: string): string => {
  if (cohort === 'CASHAPP') {
    return `${app.CASH_COHORT_OFFER_LINK_URL}?client_id=${api.CASH_API_CLIENT_ID}&reference_id=${token}`
  }
  return `${app.AP_COHORT_OFFER_LINK_URL}/${token}`
}

const getLaOffsetForDate = (dateString: string): string => {
  const [y, m, d] = dateString.split('-').map(Number)
  const middayUtcMs = Date.UTC(y, m - 1, d, 12, 0, 0)
  const tzName = new Intl.DateTimeFormat('en-US', {
    timeZone: APPLE_PAYLATER_TIMEZONE,
    timeZoneName: 'short',
  })
    .format(new Date(middayUtcMs))
    .split(' ')
    .pop()

  return tzName === 'PDT' ? '-07:00' : '-08:00'
}

const formatDateForAppleGateway = (dateString: string): string => {
  const offset = getLaOffsetForDate(dateString)
  return `${dateString}T23:59:59${offset}`
}

const getTodayLaDateString = (): string => {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APPLE_PAYLATER_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)

  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

const isValidOffer = (offer: EnrichedOfferDetail): boolean => {
  const schedules = offer.paymentSchedules
  const hasValidSchedules =
    valueIsNotNullOrUndefined(schedules) &&
    schedules.length > 0 &&
    valueIsNotNullOrUndefined(schedules[0].installmentDueDate) &&
    valueIsNotNullOrUndefined(schedules[schedules.length - 1].installmentDueDate)
  return (
    (offer.paymentType === PaymentType.PBI || offer.paymentType === PaymentType.PCL) &&
    valueIsNotNullOrUndefined(offer.numberOfInstallments) &&
    valueIsNotNullOrUndefined(offer.monthlyAmount) &&
    valueIsNotNullOrUndefined(offer.totalAmount) &&
    valueIsNotNullOrUndefined(offer.amountDueToday) &&
    hasValidSchedules
  )
}

const mapOfferToPresentedOffer = (offer: EnrichedOfferDetail): PresentedOffer | null => {
  const firstPaymentDueDate = offer.paymentSchedules[0].installmentDueDate
  const lastPaymentSchedule = offer.paymentSchedules[offer.paymentSchedules.length - 1]

  if (!isValidString(firstPaymentDueDate)) {
    return null
  }

  const offerId = uuidv4()

  const interestDetails: InterestDetails | undefined =
    valueIsNotNullOrUndefined(offer.apr) && offer.apr !== '' && valueIsNotNullOrUndefined(offer.interestAmount)
      ? {
          aprInterest: {
            percentage: (parseFloat(offer.apr) / 100).toFixed(4),
            totalInterest: offer.interestAmount,
          },
        }
      : undefined

  const downPaymentDueDate = parseFloat(offer.amountDueToday.amount) > 0 ? firstPaymentDueDate : getTodayLaDateString()

  return {
    offerId,
    numberOfInstallments: offer.numberOfInstallments,
    recurringInstallmentAmount: offer.monthlyAmount,
    offerType: offer.paymentType as PaymentType.PBI | PaymentType.PCL,
    period: offer.paymentType === PaymentType.PBI ? ('daily' as const) : ('monthly' as const),
    total: offer.totalAmount,
    interval: offer.paymentType === PaymentType.PBI ? 14 : 1,
    downPayment: {
      amount: offer.amountDueToday.amount,
      currency: offer.amountDueToday.currency,
      dueBy: formatDateForAppleGateway(downPaymentDueDate),
    },
    firstInstallmentDueDate: formatDateForAppleGateway(firstPaymentDueDate),
    finalInstallmentAmount: {
      amount: lastPaymentSchedule.amount.amount,
      currency: lastPaymentSchedule.amount.currency,
    },
    ...(valueIsNotNullOrUndefined(interestDetails) && { interestDetails }),
  }
}

const isMatchingOffer = (offer: EnrichedOfferDetail, selectedOffer: EnrichedOfferDetail): boolean =>
  offer.paymentType === selectedOffer.paymentType &&
  offer.numberOfInstallments === selectedOffer.numberOfInstallments &&
  offer.totalAmount.amount === selectedOffer.totalAmount.amount &&
  offer.monthlyAmount.amount === selectedOffer.monthlyAmount.amount

export const buildSaveOfferParams = (
  token: string,
  consumer: ConsumerState,
  offers: EnrichedOfferDetail[] | undefined,
  selectedOffer: EnrichedOfferDetail | undefined,
  userCohort: UserCohort,
  expires?: string
): AppleGatewaySaveOfferRequest | null => {
  const uuid = consumer.uuid
  const mobile = consumer.mobile
  if (
    !isValidString(uuid) ||
    !isValidString(mobile) ||
    !valueIsNotNullOrUndefined(offers) ||
    offers.length === 0 ||
    !valueIsNotNullOrUndefined(selectedOffer)
  ) {
    return null
  }

  const validOffers = offers.filter(isValidOffer)
  const presentedOffers: PresentedOffer[] = validOffers.map(mapOfferToPresentedOffer).filter(valueIsNotNullOrUndefined)

  if (presentedOffers.length === 0) {
    return null
  }

  const selectedIndex = validOffers.findIndex((offer) => isMatchingOffer(offer, selectedOffer))
  const selectedOfferId = selectedIndex >= 0 ? presentedOffers[selectedIndex].offerId : presentedOffers[0].offerId

  return {
    token,
    userWasDeclined: false,
    userCohort,
    userId: uuid,
    phoneNumber: mobile,
    offerDetails: {
      presentedOffer: presentedOffers,
      selectedOfferId,
      selectedOfferLink: buildSelectedOfferLink(userCohort, token),
      ...(valueIsNotNullOrUndefined(expires) && {
        validUntil: formatUtcToTimezoneOffset(expires, APPLE_PAYLATER_TIMEZONE),
      }),
    },
  }
}

export const buildIssueCardParams = (token: string, consumer: ConsumerState): AppleGatewayIssueCardRequest | null => {
  if (!isValidString(consumer.uuid)) {
    return null
  }

  return {
    token,
    consumer: {
      consumerUuid: consumer.uuid,
    },
  }
}
